import { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, ImageOff, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtPrice, mediaUrl } from "@/lib/api";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { WatchClientsBadge, WatchClientsPanel } from "@/pages/admin/WatchClients";
import { TemplatesButton } from "@/pages/admin/WatchTemplates";

export const STATUS_LABELS = { draft: "Rascunho", published: "Publicado", reserved: "Reservado", sold: "Vendido" };
export const AVAIL_LABELS = { in_stock: "Em stock", reserved: "Reservado", on_order: "Por encomenda" };

export default function Watches() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const status = sp.get("estado") || "";
  const load = () => api.get("/admin/watches", { params: { status: status || undefined, q: q || undefined } }).then((r) => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);
  const quick = async (id, s) => {
    try { await api.patch(`/admin/watches/${id}/status`, { status: s }); toast.success("Estado atualizado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const share = async (w) => {
    try {
      const { data } = await api.post(`/admin/watches/${w.id}/share`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Ligação copiada para a área de transferência");
      } catch {
        window.prompt("Copie a ligação de partilha:", data.url);
      }
      setItems((xs) => xs.map((x) => (x.id === w.id ? { ...x, share_count: data.count, last_shared_at: data.at } : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-watches">
      <PageTitle title="Relógios" sub={`${items.length} registos`}>
        <TemplatesButton />
        <Link to="/admin/relogios/novo" className="btn-gold !py-2.5" data-testid="admin-new-watch-button"><Plus className="w-4 h-4" /> Novo relógio</Link>
      </PageTitle>
      <div className="flex flex-wrap gap-3 mb-5">
        <select className="afield !w-auto" value={status} onChange={(e) => setSp(e.target.value ? { estado: e.target.value } : {})} data-testid="admin-watches-status-filter">
          <option value="">Todos os estados</option>{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-[200px]"><input className="afield" placeholder="Pesquisar modelo ou referência…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-watches-search" /></form>
      </div>
      <div className="overflow-x-auto border border-[#282520]">
        <table className="w-full text-sm" data-testid="admin-watches-table">
          <thead className="bg-[#161512] text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">
            <tr>{["", "Relógio", "Preço", "Stock", "Disponibilidade", "Clientes", "Estado", "Partilhas", ""].map((h, i) => <th key={i} className="text-left font-normal px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((w) => {
              const img = w.media.find((m) => m.type === "photo" && m.is_main) || w.media.find((m) => m.type === "photo");
              const canShare = w.status === "published" || w.status === "reserved";
              return (
                <Fragment key={w.id}>
                <tr className="border-t border-[#282520] hover:bg-white/[0.02]" data-testid={`admin-watch-row-${w.id}`}>
                  <td className="px-4 py-3 w-16">{img ? <img src={mediaUrl(img.sm)} alt="" className="w-12 h-14 object-cover" /> : <div className="w-12 h-14 bg-[#161512] flex items-center justify-center"><ImageOff className="w-4 h-4 text-[#A39E93]" /></div>}</td>
                  <td className="px-4 py-3"><Link to={`/admin/relogios/${w.id}`} className="hover:text-[#E6D5AC]" data-testid={`admin-watch-edit-${w.id}`}><span className="block text-[10px] uppercase tracking-[0.2em] text-[#C8AE6E]">{w.brand}</span>{w.model} <span className="text-[#A39E93] text-xs">{w.reference}</span></Link>{w.is_sample && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#A39E93] border border-[#282520] px-1.5">exemplo</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{w.price_on_request || !w.price_cents ? <span className="text-[#A39E93]">Sob consulta</span> : fmtPrice(w.promo_price_cents || w.price_cents)}</td>
                  <td className="px-4 py-3">{w.stock_qty}</td>
                  <td className="px-4 py-3 text-[#A39E93]">{w.status === "draft" || w.status === "sold" ? "—" : AVAIL_LABELS[w.availability]}</td>
                  <td className="px-4 py-3"><WatchClientsBadge w={w} open={openId === w.id} onToggle={() => setOpenId(openId === w.id ? null : w.id)} /></td>
                  <td className="px-4 py-3">
                    <select className="afield !w-auto !py-1 text-xs" value={w.status} onChange={(e) => quick(w.id, e.target.value)} data-testid={`admin-watch-status-${w.id}`}>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-xs tabular-nums text-[#A39E93]" data-testid={`admin-watch-share-count-${w.id}`}>{w.share_count || 0}</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => share(w)} disabled={!canShare} title={canShare ? "Copiar ligação pública e contar partilha" : "Só é possível partilhar relógios publicados ou reservados"} className="btn-line !py-1.5 !px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5" data-testid={`admin-watch-share-${w.id}`}>
                      <Share2 className="w-3.5 h-3.5" strokeWidth={1.4} /> Partilhar
                    </button>
                  </td>
                </tr>
                {openId === w.id && <tr className="border-t border-[#282520]"><td colSpan={9} className="p-0"><WatchClientsPanel w={w} reload={load} /></td></tr>}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
